import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import BIReportUploadModal from '../components/BIReportUploadModal';
import OrderBookTable from '../components/OrderBookTable';
import HOSupportView from '../components/HOSupportView';
import { ContactNotesTasksModal } from '../components/ContactNotesTasksModal';
import ParsedOrderViewModal from '../components/ParsedOrderViewModal';
import { useBranch } from '../context/BranchContext';
import api from '../services/api';
import { API } from '../App';
import { ArrowUp, ChevronDown, BarChart3, UploadCloud, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const enconOrange = '#ee7203';

// Month picker range for the transport-focused filters — a fixed rolling window
// is simplest and avoids an extra API round-trip just to discover which months
// have data; "All" (the default) always shows everything regardless.
const MONTH_OPTIONS = (() => {
  const opts = [{ value: 'all', label: 'All' }];
  const now = new Date();
  for (let i = -3; i <= 11; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    opts.push({ value, label });
  }
  return opts;
})();

// Registry of available BI reports — extend this as new reports are added.
const REPORTS = {
  order_book: {
    label: 'Order Book',
    statusKey: 'order_book_summary',
    m4ReportName: 'M4 reports - SO - Sales Order Book Summary',
  },
  head_office_support: {
    label: 'Head Office Support',
    statusKey: 'ho_support_monthly',
    m4ReportName: 'the monthly "30 - HO Support" export',
  },
};

const FILTERS = [
  { key: 'all',               label: 'All Orders' },
  { key: 'amendments',        label: 'Amendments' },
  { key: 'low_contribution',  label: 'Low Contribution' },
  { key: 'not_on_transport',  label: 'Not on transport' },
  { key: 'transport_info',    label: 'Transport Info' },
];

export default function BIReportsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const { getEffectiveBranchId } = useBranch();
  const [selectedReport, setSelectedReport] = useState('order_book');
  const [activeFilter, setActiveFilter] = useState('all');
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [lowContributionPct, setLowContributionPct] = useState(6);
  const [pctInput, setPctInput] = useState('6');
  const [transportMonth, setTransportMonth] = useState('all');

  // Customer / order drill-through modals
  const [custSearch, setCustSearch] = useState(null);
  const [orderView, setOrderView] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  const reportConfig = REPORTS[selectedReport];
  const isOrderBook = selectedReport === 'order_book';

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await api.get(`${API}/order-book-reports/status?report=${reportConfig.statusKey}`);
      setStatus(res.data);
    } catch {
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [reportConfig.statusKey]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const [hoUploadRefreshSignal, setHoUploadRefreshSignal] = useState(0);

  const handleModalClose = () => {
    setShowUploadModal(false);
    loadStatus();
    if (!isOrderBook) setHoUploadRefreshSignal((v) => v + 1);
  };

  const handleOpenOrder = async (orderNumber) => {
    setOrderLoading(true);
    try {
      const res = await api.get(`${API}/automations/orders/${orderNumber}`);
      setOrderView(res.data);
    } catch {
      toast.error(`No parsed order document found for #${orderNumber}`);
    } finally {
      setOrderLoading(false);
    }
  };

  const subHeaderLeft = (
    <>
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white/90 hover:text-white transition-colors"
        style={{ background: 'rgba(0,0,0,0.22)' }}
        data-testid="bi-reports-scroll-top-btn"
      >
        <ArrowUp className="w-3.5 h-3.5" />
      </button>
      <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
        <BarChart3 className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
        <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">BI Reports</span>
      </div>
      <div className="relative flex-shrink-0">
        <select
          value={selectedReport}
          onChange={(e) => setSelectedReport(e.target.value)}
          data-testid="bi-reports-report-select"
          className="appearance-none bg-white text-gray-700 text-xs sm:text-sm font-medium rounded-full pl-3 pr-7 py-1.5 border border-gray-200 cursor-pointer shadow-sm"
        >
          {Object.entries(REPORTS).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
    </>
  );

  // The Amendments/Low Contribution/Not on Transport/Transport Info filter
  // controls only apply to the Order Book report — Head Office Support has
  // its own sub-tabs (New Claims / Live Accounts / Supplier Matrix) rendered
  // inside HOSupportView instead, so this whole row is skipped for it.
  const subHeaderCenter = !isOrderBook ? null : (
    <>
      <div className="relative">
        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          data-testid="bi-reports-filter-select"
          className="appearance-none bg-white text-gray-700 text-xs sm:text-sm font-semibold rounded-full pl-4 pr-8 py-1.5 border-2 cursor-pointer shadow-sm"
          style={{ borderColor: enconOrange }}
        >
          {FILTERS.map((f) => (
            <option key={f.key} value={f.key}>{f.label}</option>
          ))}
        </select>
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
      </div>
      {activeFilter === 'low_contribution' && (
        <div
          className="flex items-center gap-1 bg-white rounded-full pl-3 pr-1.5 py-1.5 shadow-sm border-2"
          style={{ borderColor: enconOrange }}
          title="Contribution % threshold — click out to re-filter"
        >
          <span className="text-xs font-semibold text-gray-500">&lt;</span>
          <input
            type="number"
            min={0}
            max={100}
            step={0.5}
            value={pctInput}
            onChange={(e) => setPctInput(e.target.value)}
            onBlur={() => {
              const parsed = parseFloat(pctInput);
              const clamped = isNaN(parsed) ? 6 : Math.min(100, Math.max(0, parsed));
              setPctInput(String(clamped));
              setLowContributionPct(clamped);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            className="w-12 text-xs sm:text-sm font-semibold text-gray-800 text-center focus:outline-none"
            data-testid="bi-reports-low-contribution-pct-input"
          />
          <span className="text-xs font-semibold text-gray-500">%</span>
        </div>
      )}
      {(activeFilter === 'transport_info' || activeFilter === 'not_on_transport') && (
        <div className="relative">
          <select
            value={transportMonth}
            onChange={(e) => setTransportMonth(e.target.value)}
            data-testid="bi-reports-transport-month-select"
            title="Filter by delivery month — 'All' shows every month"
            className="appearance-none bg-white text-gray-700 text-xs sm:text-sm font-medium rounded-full pl-3 pr-7 py-1.5 border-2 cursor-pointer shadow-sm"
            style={{ borderColor: enconOrange }}
          >
            {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      )}
    </>
  );

  const subHeaderRight = (
    statusLoading ? (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/20 text-white text-xs">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…
      </div>
    ) : status?.uploaded_today ? (
      <button
        onClick={() => setShowUploadModal(true)}
        data-testid="bi-reports-status-pill-uploaded"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-green-700 text-xs sm:text-sm font-semibold shadow-sm hover:shadow-md transition-shadow"
      >
        <CheckCircle2 className="w-3.5 h-3.5" /> {reportConfig.label} updated today
      </button>
    ) : (
      <button
        onClick={() => setShowUploadModal(true)}
        data-testid="bi-reports-status-pill-needed"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-red-600 text-xs sm:text-sm font-semibold shadow-sm hover:shadow-md transition-shadow animate-pulse"
      >
        <UploadCloud className="w-3.5 h-3.5" /> Upload {reportConfig.label} report
      </button>
    )
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <AppHeader
        user={user}
        onLogout={onLogout}
        subHeaderLeft={subHeaderLeft}
        subHeaderCenter={subHeaderCenter}
        subHeaderRight={subHeaderRight}
      />

      {/* ── Scrollable content ────────────────────────────────────────────────── */}
      <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 pt-4 pb-8">
        {orderLoading && (
          <div className="flex items-center gap-2 text-sm text-gray-500 px-1 py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading order…
          </div>
        )}
        {isOrderBook ? (
          <OrderBookTable
            filter={activeFilter}
            lowContributionPct={lowContributionPct}
            month={transportMonth}
            branchId={getEffectiveBranchId()}
            onOpenCustomer={(name) => setCustSearch(name)}
            onOpenOrder={handleOpenOrder}
          />
        ) : (
          <HOSupportView onOpenUpload={() => setShowUploadModal(true)} refreshSignal={hoUploadRefreshSignal} />
        )}
      </div>

      <BIReportUploadModal
        isOpen={showUploadModal}
        onClose={handleModalClose}
        reportConfig={reportConfig}
      />

      <ContactNotesTasksModal
        isOpen={!!custSearch}
        onClose={() => setCustSearch(null)}
        initialSearch={custSearch}
      />

      {orderView && (
        <ParsedOrderViewModal order={orderView} onClose={() => setOrderView(null)} />
      )}
    </div>
  );
}
